import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoComponentShape,
  GeckoComponentSpace,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Returns the preferred axis names (for example, "Position" and "Elevation" or
 * "Angle" and "Radius") for a given geometry and component type
 * @param geometry            The geometry to get preferred axis names for
 * @param componentModelType  The component type we're getting axis names for
 */
export const useAxisNames = (
  geometry: GeckoComponentShape | GeckoComponentSpace | undefined,
  componentModelType: GeckoComponentModelType | undefined
) =>
  React.useMemo(() => {
    if (!geometry) return { xAxisName: 'Position', yAxisName: 'Elevation' }
    if (componentModelType === GeckoComponentModelType.Dome) {
      return { xAxisName: 'Latitude', yAxisName: 'Longitude' }
    }
    if (
      geometry.x.unit === GeckoMeasurementUnit.Degrees ||
      geometry.x.unit === GeckoMeasurementUnit.Radians
    )
      return { xAxisName: 'Angle', yAxisName: 'Radius' }
    else if (geometry.x.unit === GeckoMeasurementUnit.Tubes)
      return {
        // Tubes are odd in that a range from tube 1 to tube 2... is just tube 1
        xAxisName:
          geometry instanceof GeckoComponentCoords ||
          (geometry instanceof GeckoComponentRect && geometry.width.num <= 1.0)
            ? 'Tube'
            : 'Tubes',
        yAxisName: 'Elevation',
      }
    else if (componentModelType === GeckoComponentModelType.ShipDeck) {
      return {
        xAxisName:
          'Distance From Centerline' +
          (geometry instanceof GeckoComponentSpace
            ? ''
            : ` ${getShipDeckXAxisSuffix(geometry)}`),
        yAxisName: 'Frame',
      }
    }
    return { xAxisName: 'Position', yAxisName: 'Elevation' }
  }, [componentModelType, geometry])

function getShipDeckXAxisSuffix(geometry: GeckoComponentShape) {
  function _getSideOfShip(value: number) {
    return value < 0 ? 'Starboard' : 'Port'
  }

  if (geometry instanceof GeckoComponentCoords) {
    return `(${_getSideOfShip(geometry.x.num)})`
  }
  return `(${_getSideOfShip(geometry.x.min.num)} - ${_getSideOfShip(geometry.x.max.num)})`
}
